import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import { applyClasses, applyProps } from '../shared/js';
import styles from './radio-button.module.scss';

const RadioButton = ({
  id,
  name,
  value,
  children,
  validation,
  checked,
  disabled,
  ...props
}) => {
  const classes = applyClasses(props, styles);
  const formMethods = useFormContext();
  const defaultChecked =
    get(formMethods?.getValues(), name) === value || checked;
  const register = formMethods?.register ? formMethods?.register : () => {};
  const formProps = register(name, { ...validation });

  const errors = formMethods?.formState.errors;
  const error = errors && get(errors, name);

  const handleChange = (e) => {
    formProps?.onChange(e);
    props.onChange && props.onChange(e.target.value);
  };

  return (
    <div
      // eslint-disable-next-line react/prop-types
      className={cn(styles.radioButton, props.className, {
        [styles.disabled]: disabled,
      })}
    >
      <label id={id} className={cn(styles.label, classes)}>
        <input
          className={styles.input}
          type="radio"
          name={name}
          defaultChecked={defaultChecked}
          value={value}
          disabled={disabled}
          {...formProps}
          {...applyProps(props)}
          onChange={handleChange}
        />
        <span className={cn(styles.button, classes)} />
        <div className={styles.text}>{children}</div>
      </label>
      {error && (
        <span role="alert">
          {error.message || (
            <FormattedMessage
              id="forms.error"
              description="Error message"
              defaultMessage="Field error"
            />
          )}
        </span>
      )}
    </div>
  );
};

RadioButton.propTypes = {
  /** The ID of the input label. Useful to choose the input during testing. */
  id: PropTypes.string,
  /** The name of the nested input element */
  name: PropTypes.string,
  /** A callback function called when the button is checked or unchecked */
  onChange: PropTypes.func,
  /** The content that should make up the radio button label  */
  children: PropTypes.any,
  /** The value of the checkbox */
  value: PropTypes.string,
  /** The validation rules for the input, see https://react-hook-form.com/get-started#Applyvalidation */
  validation: PropTypes.object,
  /** The form errors object */
  errors: PropTypes.object,
  /** The default state of the radio button */
  checked: PropTypes.bool,
  /** Disable the input */
  disabled: PropTypes.bool,
};

RadioButton.defaultProps = {
  name: undefined,
  onChange: () => {},
  children: undefined,
  ariaLabel: undefined,
  value: undefined,
  primary: undefined,
  secondary: undefined,
  validation: {},
  errors: {},
  checked: false,
  disabled: false,
};

export default RadioButton;
